/**
 * `/admin` (PRD §4.5) — G-Admin back-office across tenants.
 */

import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Download, FileText, Loader2, RefreshCw } from "lucide-react";
import { MonthLockPanel } from "@/components/tracking/month-lock-panel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GroupedProjectSelectItems } from "@/components/grouped-project-select-items";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { tabSegmentedInactiveClass } from "@/components/ui/tabs";
import { LegalSettingsSection } from "@/components/admin/legal-settings";
import { YearSelect } from "@/components/ui/year-select";
import {
  coercePageSize,
  DEFAULT_PAGE_SIZE,
  pageCount,
  PaginationBar,
  type PageSizeOption,
} from "@/components/ui/pagination-bar";
import { usePatchQueryParams, useQueryFlag, useQueryParam, useQueryYear } from "@/lib/query-param";
import { api, saveBlob } from "@/lib/api";
import { useQueryTab } from "@/lib/query-tab";
import {
  type AdminProject,
  type AdminUser,
  type AdminWeekNote,
  type AuditEventRow,
  type EntriesAuditRow,
  type MatrixStatus,
  type MonthMatrix,
  useAdminAuditChain,
  useAdminAuditEvents,
  useAdminEmailLog,
  useAdminEntriesAudit,
  useAdminImportBatches,
  useAdminMagicLink,
  useAdminMatrix,
  useAdminPasswordReset,
  useAdminProjects,
  useAdminSetUserEnabled,
  useAdminSettings,
  useAdminUpdateSetting,
  useAdminUsers,
  useAdminWeekNotes,
  useDownloadAdminMatrixXlsx,
  useDownloadAuditEventsCsv,
  useDownloadAuditXlsx,
  useDownloadHoursTemplate,
  useHoursImportCommit,
  useHoursImportPreview,
  type HoursImportPreview,
} from "@/lib/admin";
import { describeError } from "@/lib/errors";
import {
  formatDate,
  formatPersonName,
  formatProjectTerm,
  formatProjectTermLine,
} from "@/lib/format";
import { filterArchived, useCompanies } from "@/lib/tenancy";
import { AUDIT_EVENT_TYPES } from "@/shared/audit-event-types";
import { berlinToday, isBusinessDate } from "@/shared/dates";
import { clampToYears, projectYears } from "@/shared/years";

const ADMIN_TABS = [
  "users",
  "entries",
  "email",
  "notes",
  "locks",
  "audit",
  "system-logs",
  "settings",
  "imports",
] as const;

type AdminTab = (typeof ADMIN_TABS)[number];

const ADMIN_TAB_I18N: Record<AdminTab, string> = {
  users: "admin.tabUsers",
  entries: "admin.tabEntries",
  email: "admin.tabEmail",
  notes: "admin.tabWeekNotes",
  locks: "admin.tabLocks",
  audit: "admin.tabAudit",
  "system-logs": "admin.tabSystemLogs",
  settings: "admin.tabSettings",
  imports: "admin.tabImports",
};

export function AdminPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useQueryTab("tab", ADMIN_TABS, "users");

  // Legacy ?tab=matrix → ?tab=entries (label was already "Entries").
  // Legacy ?tab=projects → ?tab=users (Projects tab removed).
  useEffect(() => {
    const raw = params.get("tab");
    if (raw !== "matrix" && raw !== "projects") return;
    const next = new URLSearchParams(params);
    next.set("tab", raw === "matrix" ? "entries" : "users");
    setParams(next, { replace: true });
  }, [params, setParams]);

  return (
    <div>
      <div>
        <div className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
          {t("admin.kicker")}
        </div>
        <h1 className="mt-2 text-4xl">{t("admin.title")}</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{t("admin.subtitle")}</p>
      </div>

      <div
        className="mt-2 flex flex-wrap gap-1 rounded-lg border border-border bg-muted p-1 text-sm text-muted-foreground"
        data-testid="admin-tab-strip"
      >
        {ADMIN_TABS.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setTab(value)}
            className={`flex-1 rounded-md px-3 py-2 transition ${
              tab === value ? "bg-card text-foreground shadow-soft" : tabSegmentedInactiveClass
            }`}
          >
            {t(ADMIN_TAB_I18N[value])}
          </button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "users" && <UsersTab />}
        {tab === "entries" && <EntriesTab />}
        {tab === "email" && <EmailTab />}
        {tab === "notes" && <WeekNotesTab />}
        {tab === "locks" && <LocksTab />}
        {tab === "audit" && <AuditTab />}
        {tab === "system-logs" && <SystemLogsTab />}
        {tab === "settings" && <SettingsTab />}
        {tab === "imports" && <ImportsTab />}
      </div>
    </div>
  );
}

function adminTableClass(extra = ""): string {
  return `rounded-2xl border border-border bg-card ${extra}`.trim();
}

function auditMetaHasKeys(meta: unknown): boolean {
  return Boolean(
    meta &&
    typeof meta === "object" &&
    !Array.isArray(meta) &&
    Object.keys(meta as Record<string, unknown>).length > 0,
  );
}

function prettyAuditMeta(meta: unknown): string {
  try {
    return JSON.stringify(meta, null, 2);
  } catch {
    return String(meta);
  }
}

function filterAdminUsers(users: AdminUser[], search: string): AdminUser[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return users;
  return users.filter((user) =>
    `${formatPersonName(user)} ${user.email} ${user.companyName ?? ""}`
      .toLowerCase()
      .includes(needle),
  );
}

function UsersTab() {
  const { t } = useTranslation();
  const [showArchived, setShowArchived] = useQueryFlag("archived");
  const [search, setSearch] = useState("");
  const users = useAdminUsers({ includeArchived: true });
  const list = useMemo(
    () => filterAdminUsers(filterArchived(users.data ?? [], showArchived), search),
    [users.data, showArchived, search],
  );
  const coldLoading = users.isLoading && !users.data;
  const setEnabled = useAdminSetUserEnabled();
  const magicLink = useAdminMagicLink();
  const passwordReset = useAdminPasswordReset();

  function toggleEnabled(user: AdminUser) {
    setEnabled.mutate(
      { id: user.id, isEnabled: !user.isEnabled },
      {
        onSuccess: () =>
          toast.success(t(user.isEnabled ? "adminExtra.userDisabled" : "adminExtra.userEnabled")),
        onError: (error) => toast.error(describeError(error)),
      },
    );
  }

  function sendMagicLink(user: AdminUser) {
    magicLink.mutate(user.id, {
      onSuccess: () => toast.success(t("adminExtra.magicLinkSent")),
      onError: (error) => toast.error(describeError(error)),
    });
  }

  function sendPasswordReset(user: AdminUser) {
    passwordReset.mutate(user.id, {
      onSuccess: () => toast.success(t("adminExtra.passwordResetSent")),
      onError: (error) => toast.error(describeError(error)),
    });
  }

  const busy = setEnabled.isPending || magicLink.isPending || passwordReset.isPending;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Input
          className="w-64"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("employees.searchPlaceholder")}
          aria-label={t("employees.search")}
          data-testid="admin-users-search"
        />
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Checkbox
            checked={showArchived}
            onCheckedChange={(value) => setShowArchived(value === true)}
          />
          {t("adminExtra.showArchived")}
        </label>
      </div>

      {coldLoading && <Skeleton className="mt-4 h-40 w-full rounded-xl" />}

      {!coldLoading && (
        <div className={`mt-4 overflow-x-auto ${adminTableClass()}`}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("employees.colName")}</TableHead>
                <TableHead>{t("adminExtra.colCompany")}</TableHead>
                <TableHead>{t("adminExtra.colRole")}</TableHead>
                <TableHead>{t("adminExtra.colEnabled")}</TableHead>
                <TableHead>{t("adminExtra.colLastLogin")}</TableHead>
                <TableHead>{t("adminExtra.colLastEntry")}</TableHead>
                <TableHead>{t("adminExtra.colCreatedAt")}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-muted-foreground">
                    {t("adminExtra.usersEmpty")}
                  </TableCell>
                </TableRow>
              )}
              {list.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="font-medium">{formatPersonName(user)}</div>
                    <div className="text-xs text-muted-foreground">{user.email}</div>
                    {user.archived && (
                      <Badge variant="secondary" className="mt-1">
                        {t("adminExtra.archivedBadge")}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{user.companyName ?? "—"}</TableCell>
                  <TableCell>{t(`appShell.role.${user.role}`, user.role)}</TableCell>
                  <TableCell>
                    {user.isEnabled ? t("adminExtra.enabled") : t("adminExtra.disabled")}
                  </TableCell>
                  <TableCell>{formatTimestamp(user.lastLoginAt)}</TableCell>
                  <TableCell>{formatDate(user.lastEntryAt)}</TableCell>
                  <TableCell>{formatTimestamp(user.createdAt)}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy || user.archived}
                        onClick={() => toggleEnabled(user)}
                      >
                        {user.isEnabled ? t("adminExtra.disable") : t("adminExtra.enable")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy || user.archived}
                        onClick={() => sendMagicLink(user)}
                      >
                        {t("adminExtra.magicLink")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy || user.archived}
                        onClick={() => sendPasswordReset(user)}
                      >
                        {t("adminExtra.passwordReset")}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

/**
 * The term of the picked project and, when switching projects left the wanted year outside it,
 * which year is on screen instead. Without this an out-of-term year reads as "nothing tracked".
 */
function ProjectYearContext({
  project,
  requestedYear,
  shownYear,
  className,
  showYearAdjusted = true,
}: {
  project: AdminProject;
  requestedYear: number;
  shownYear: number;
  className?: string;
  /** When false, only the project term line is rendered (caller places yearAdjusted elsewhere). */
  showYearAdjusted?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className={`space-y-1 text-sm text-muted-foreground ${className ?? ""}`}>
      <p>{formatProjectTermLine(project.startDate, project.endDate)}</p>
      {showYearAdjusted && requestedYear !== shownYear && (
        <p className="text-foreground">
          {t("adminExtra.yearAdjusted", {
            project: project.name,
            term: formatProjectTerm(project.startDate, project.endDate),
            requested: requestedYear,
            shown: shownYear,
          })}
        </p>
      )}
    </div>
  );
}

/** Hours across the whole matrix — 0 means "nothing tracked", never "nobody assigned". */
function trackedHours(matrix: MonthMatrix): number {
  return matrix.employees.reduce(
    (sum, employee) => sum + employee.months.reduce((total, cell) => total + cell.hours, 0),
    0,
  );
}

function EntriesTab() {
  const { t, i18n } = useTranslation();
  const todayYear = Number(berlinToday().slice(0, 4));
  const projects = useAdminProjects();
  const projectIds = (projects.data ?? []).map((row) => row.id);
  const [projectId, setProjectId] = useQueryParam("project", {
    fallback: "",
    allowed: projectIds.length > 0 ? projectIds : undefined,
  });
  const selected = projectId || projects.data?.[0]?.id || "";
  const project = (projects.data ?? []).find((row) => row.id === selected);
  const years = project ? projectYears(project.startDate, project.endDate, todayYear) : [];
  const [year, setYear] = useQueryYear("year", years, todayYear);
  // The picker offers the term only, so the query can never ask for a year the project never had.
  const shownYear = clampToYears(year, years);
  const matrix = useAdminMatrix(selected || undefined, shownYear);
  const downloadXlsx = useDownloadAdminMatrixXlsx();

  const monthsShort = Array.from({ length: 12 }, (_, i) =>
    new Date(2024, i, 1).toLocaleDateString(i18n.language === "en" ? "en-GB" : "de-DE", {
      month: "short",
    }),
  );

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">{t("adminExtra.selectProject")}</Label>
          <Select value={selected} onValueChange={setProjectId}>
            <SelectTrigger className="min-w-[280px]" aria-label={t("adminExtra.selectProject")}>
              <SelectValue placeholder={t("adminExtra.selectProject")} />
            </SelectTrigger>
            <SelectContent>
              <GroupedProjectSelectItems projects={projects.data ?? []} />
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">{t("adminExtra.year")}</Label>
          <YearSelect
            value={shownYear}
            onChange={setYear}
            years={years}
            ariaLabel={t("adminExtra.year")}
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!selected || matrix.isFetching}
          onClick={() => void matrix.refetch()}
        >
          <RefreshCw className={`size-4 ${matrix.isFetching ? "animate-spin" : ""}`} />{" "}
          {t("common.refresh")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="ml-auto"
          disabled={!selected || downloadXlsx.isPending}
          onClick={() =>
            downloadXlsx.mutate(
              { projectId: selected, year: shownYear },
              {
                onSuccess: () => toast.success(t("admin.matrixDownloadDone")),
                onError: (error) => toast.error(describeError(error)),
              },
            )
          }
        >
          {downloadXlsx.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Download className="size-4" />
          )}{" "}
          {t("admin.matrixDownloadXlsx")}
        </Button>
      </div>

      {project && (
        <ProjectYearContext
          className="mb-4"
          project={project}
          requestedYear={year}
          shownYear={shownYear}
          showYearAdjusted={false}
        />
      )}

      {matrix.isLoading && <Skeleton className="mt-2 h-40 w-full rounded-xl" />}

      {!matrix.isLoading && project && (
        <div className={`${adminTableClass()} space-y-4 p-4`}>
          {year !== shownYear && (
            <p className="text-sm text-foreground">
              {t("adminExtra.yearAdjusted", {
                project: project.name,
                term: formatProjectTerm(project.startDate, project.endDate),
                requested: year,
                shown: shownYear,
              })}
            </p>
          )}

          {matrix.data && trackedHours(matrix.data) === 0 && (
            <p className="text-sm text-muted-foreground">
              {matrix.data.employees.length === 0
                ? t("adminExtra.noAssignees")
                : t("admin.matrixNoHours", {
                    count: matrix.data.employees.length,
                    year: shownYear,
                  })}
            </p>
          )}

          {matrix.data && matrix.data.employees.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                    <th className="px-4 py-2 font-medium">{t("projectLive.employee")}</th>
                    {monthsShort.map((label) => (
                      <th key={label} className="px-2 py-2 text-center font-medium">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.data.employees.map((employee) => (
                    <tr key={employee.userId} className="border-t border-border">
                      <td className="px-4 py-2">
                        <div className="font-medium">{formatPersonName(employee)}</div>
                        <div className="text-[10px] text-muted-foreground">{employee.email}</div>
                      </td>
                      {employee.months.map((cell) => (
                        <td key={cell.month} className="px-2 py-2 text-center">
                          <MatrixCellBadge
                            status={cell.status}
                            hours={cell.hours}
                            title={`${cell.hours.toFixed(1)}h (${cell.status})`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!matrix.isLoading && matrix.data && matrix.data.employees.length > 0 && <MatrixLegend />}
    </div>
  );
}

function MatrixCellBadge({
  status,
  title,
  hours,
}: {
  status: MatrixStatus;
  title: string;
  hours: number;
}) {
  const { t } = useTranslation();
  const colors: Record<MatrixStatus, string> = {
    full: "bg-green-100 text-green-800",
    partial: "bg-amber-100 text-amber-900",
    empty: "bg-muted text-muted-foreground",
  };
  return (
    <span
      title={title}
      className={`inline-flex min-w-[52px] items-center justify-center rounded-md px-2 py-1 text-xs tabular-nums ${colors[status]}`}
      aria-label={t(`adminExtra.status${status.charAt(0).toUpperCase()}${status.slice(1)}`)}
    >
      {hours > 0 ? hours.toFixed(0) : "—"}
    </span>
  );
}

function MatrixLegend() {
  const { t } = useTranslation();
  const items: MatrixStatus[] = ["full", "partial", "empty"];
  return (
    <div className="mt-4 flex flex-wrap gap-4 text-sm text-muted-foreground">
      {items.map((status) => (
        <span key={status} className="inline-flex items-center gap-2">
          <MatrixCellBadge status={status} title="" hours={status === "empty" ? 0 : 8} />
          {t(`adminExtra.status${status.charAt(0).toUpperCase()}${status.slice(1)}`)}
        </span>
      ))}
    </div>
  );
}

function WeekNotesTab() {
  const { t, i18n } = useTranslation();
  const todayYear = Number(berlinToday().slice(0, 4));
  const projects = useAdminProjects();
  const projectIds = (projects.data ?? []).map((row) => row.id);
  const [projectId, setProjectId] = useQueryParam("project", {
    fallback: "",
    allowed: projectIds.length > 0 ? projectIds : undefined,
  });
  const project = (projects.data ?? []).find((row) => row.id === projectId);
  const years = project ? projectYears(project.startDate, project.endDate, todayYear) : [];
  const [year, setYear] = useQueryYear("year", years, todayYear);
  const shownYear = clampToYears(year, years);
  const notes = useAdminWeekNotes(projectId || undefined, shownYear);
  const entries: AdminWeekNote[] = notes.data ?? [];

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">{t("adminExtra.selectProject")}</Label>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="min-w-[260px]" aria-label={t("adminExtra.selectProject")}>
              <SelectValue placeholder={t("adminExtra.selectProject")} />
            </SelectTrigger>
            <SelectContent>
              <GroupedProjectSelectItems projects={projects.data ?? []} />
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">{t("adminExtra.year")}</Label>
          <YearSelect
            value={shownYear}
            onChange={setYear}
            years={years}
            ariaLabel={t("adminExtra.year")}
            className="w-24"
            disabled={!project}
          />
        </div>
      </div>

      {project && (
        <ProjectYearContext project={project} requestedYear={year} shownYear={shownYear} />
      )}

      <div className={`${adminTableClass()} p-4`}>
        <h3 className="mb-3 flex items-center gap-2 font-medium">
          <FileText className="size-4" /> {t("admin.weekNotesTitle")}
        </h3>
        {!project ? (
          <p className="text-sm text-muted-foreground">{t("admin.weekNotesPickProject")}</p>
        ) : notes.isLoading ? (
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {project.personCount === 0
              ? t("adminExtra.noAssignees")
              : t("admin.weekNotesNoneForYear", {
                  count: project.personCount,
                  year: shownYear,
                })}
          </p>
        ) : (
          <div className="max-h-[700px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card">
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="p-2">{t("admin.weekNotesColWeek")}</th>
                  <th className="p-2">{t("admin.weekNotesColEmployee")}</th>
                  <th className="p-2">{t("admin.weekNotesColDescription")}</th>
                  <th className="p-2">{t("admin.weekNotesColAttachments")}</th>
                  <th className="p-2 whitespace-nowrap">{t("admin.weekNotesColUpdated")}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((note) => (
                  <tr key={note.id} className="border-b border-border/40 align-top">
                    <td className="whitespace-nowrap p-2 font-medium">
                      {note.year}-W{String(note.isoWeek).padStart(2, "0")}
                    </td>
                    <td className="whitespace-nowrap p-2">
                      <div>{formatPersonName(note)}</div>
                      <div className="text-[10px] text-muted-foreground">{note.email}</div>
                    </td>
                    <td className="max-w-xl whitespace-pre-wrap p-2">{note.note || "—"}</td>
                    <td className="max-w-xs p-2">
                      {note.attachments.length === 0 ? (
                        "—"
                      ) : (
                        <div className="flex flex-col gap-1">
                          {note.attachments.map((file) => (
                            <button
                              key={file.id}
                              type="button"
                              className="inline-flex items-center gap-1 text-left text-sm text-primary hover:underline"
                              onClick={() =>
                                void api
                                  .download(
                                    `/users/${note.userId}/projects/${projectId}/attachments/${file.id}`,
                                  )
                                  .then(({ blob, filename }) => saveBlob(blob, filename))
                              }
                            >
                              <Download className="size-3.5 shrink-0" />
                              <span className="truncate">{file.filename}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap p-2 text-xs text-muted-foreground">
                      {new Date(note.updatedAt).toLocaleString(
                        i18n.language === "en" ? "en-US" : "de-DE",
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const EMAIL_TEMPLATE_FILTERS = ["magic_link", "invite", "password_reset", "reminder"] as const;

const EMAIL_TEMPLATE_I18N: Record<(typeof EMAIL_TEMPLATE_FILTERS)[number], string> = {
  magic_link: "admin.emailTemplateMagicLink",
  invite: "admin.emailTemplateInvite",
  password_reset: "admin.emailTemplatePasswordReset",
  reminder: "admin.emailTemplateReminder",
};

function EmailTab() {
  const { t } = useTranslation();
  const [recipientInput, setRecipientInput] = useState("");
  // Debounce recipient typing (~300ms) before refetch — avoids keystroke spam.
  const [recipient, setRecipient] = useState("");
  const [template, setTemplate] = useState("all");

  useEffect(() => {
    const handle = window.setTimeout(() => setRecipient(recipientInput.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [recipientInput]);

  const emailLog = useAdminEmailLog({
    recipient: recipient || undefined,
    template: template === "all" ? undefined : template,
  });
  const entries = emailLog.data?.entries ?? [];
  const outbox = emailLog.data?.outbox;

  return (
    <div>
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="admin-email-recipient">{t("admin.emailTo")}</Label>
          <Input
            id="admin-email-recipient"
            className="w-64"
            value={recipientInput}
            onChange={(event) => setRecipientInput(event.target.value)}
            placeholder={t("admin.emailRecipientPlaceholder")}
            aria-label={t("admin.emailTo")}
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t("admin.emailTemplate")}</Label>
          <Select value={template} onValueChange={setTemplate}>
            <SelectTrigger className="w-56" aria-label={t("admin.emailTemplate")}>
              <SelectValue placeholder={t("admin.emailTemplateAll")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("admin.emailTemplateAll")}</SelectItem>
              {EMAIL_TEMPLATE_FILTERS.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(EMAIL_TEMPLATE_I18N[value])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={emailLog.isFetching}
          onClick={() => void emailLog.refetch()}
        >
          <RefreshCw className={`size-4 ${emailLog.isFetching ? "animate-spin" : ""}`} />{" "}
          {t("common.refresh")}
        </Button>
      </div>

      {outbox && (
        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          <Badge variant="secondary">{t("admin.emailQueued", { count: outbox.queued })}</Badge>
          <Badge variant={outbox.failed > 0 ? "destructive" : "secondary"}>
            {t("admin.emailFailed", { count: outbox.failed })}
          </Badge>
          <Badge variant={outbox.dailyGuardPaused ? "destructive" : "outline"}>
            {outbox.dailyGuardPaused
              ? t("admin.emailDailyGuardPaused", {
                  sent: outbox.dailySent24h,
                  cap: outbox.dailyNormalCap,
                })
              : t("admin.emailDailyGuardOk", {
                  sent: outbox.dailySent24h,
                  cap: outbox.dailyNormalCap,
                })}
          </Badge>
        </div>
      )}

      {emailLog.isLoading && <Skeleton className="mt-4 h-40 w-full rounded-xl" />}

      {!emailLog.isLoading && (
        <div className={`mt-4 overflow-x-auto ${adminTableClass()}`}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("admin.emailCreated")}</TableHead>
                <TableHead>{t("admin.emailTo")}</TableHead>
                <TableHead>{t("admin.emailTemplate")}</TableHead>
                <TableHead>{t("admin.emailStatus")}</TableHead>
                <TableHead>{t("admin.emailError")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground">
                    {t("admin.emailEmpty")}
                  </TableCell>
                </TableRow>
              )}
              {entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{formatTimestamp(entry.createdAt)}</TableCell>
                  <TableCell>{entry.toEmail}</TableCell>
                  <TableCell>{entry.template}</TableCell>
                  <TableCell>{entry.status}</TableCell>
                  <TableCell className="max-w-xs truncate">{entry.error ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function LocksTab() {
  const { t } = useTranslation();
  const companies = useCompanies();
  const projects = useAdminProjects();
  const today = berlinToday();
  const todayYear = Number(today.slice(0, 4));
  const companyIds = (companies.data ?? []).map((row) => row.id);
  const [companyId] = useQueryParam("company", {
    fallback: "",
    allowed: companyIds.length > 0 ? companyIds : undefined,
  });
  const companyProjects = (projects.data ?? []).filter(
    (project) => project.companyId === companyId && !project.archived,
  );
  const lockProjectIds = companyProjects.map((row) => row.id);
  const [projectId, setProjectId] = useQueryParam("project", {
    fallback: "",
    allowed: lockProjectIds.length > 0 ? lockProjectIds : undefined,
  });
  const patchQueryParams = usePatchQueryParams();
  const project = companyProjects.find((row) => row.id === projectId);
  const years = project ? projectYears(project.startDate, project.endDate, todayYear) : [];
  const [year, setYear] = useQueryYear("year", years, todayYear);

  if (companies.isLoading || projects.isLoading) {
    return <Skeleton className="h-40 w-full rounded-xl" />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg">{t("admin.locksTitle")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("admin.locksBody")}</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>{t("admin.locksCompany")}</Label>
          <Select
            value={companyId || undefined}
            onValueChange={(value) => {
              patchQueryParams({ company: value, project: "" });
            }}
          >
            <SelectTrigger aria-label={t("admin.locksCompany")}>
              <SelectValue placeholder={t("admin.locksPickCompany")} />
            </SelectTrigger>
            <SelectContent>
              {(companies.data ?? []).map((company) => (
                <SelectItem key={company.id} value={company.id}>
                  {company.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>{t("admin.locksProject")}</Label>
          <Select value={projectId || undefined} onValueChange={setProjectId} disabled={!companyId}>
            <SelectTrigger aria-label={t("admin.locksProject")}>
              <SelectValue placeholder={t("admin.locksPickProject")} />
            </SelectTrigger>
            <SelectContent>
              {companyProjects.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!companyId && (
        <p className="text-sm text-muted-foreground">{t("admin.locksPickCompanyHint")}</p>
      )}
      {companyId && !project && (
        <p className="text-sm text-muted-foreground">{t("admin.locksPickProjectHint")}</p>
      )}
      {project && (
        <MonthLockPanel
          projectId={project.id}
          year={year}
          onYearChange={setYear}
          yearEditable
          projectStartDate={project.startDate}
          projectEndDate={project.endDate}
          history
          canUnlock
        />
      )}
    </div>
  );
}

function auditSnap(snap: Record<string, unknown> | null | undefined, key: string): string {
  if (!snap) return "–";
  const value = snap[key];
  return value == null ? "–" : String(value);
}

function auditDayCell(row: EntriesAuditRow): string {
  if (row.entryDate) return row.entryDate;
  const snap = row.newRow ?? row.oldRow;
  if (!snap) return "–";
  if (row.source === "week_note" && snap.year != null && snap.iso_week != null) {
    return `${snap.year}-W${String(snap.iso_week).padStart(2, "0")}`;
  }
  if (row.source === "month_lock" && snap.year != null && snap.month != null) {
    return `${snap.year}-${String(snap.month).padStart(2, "0")}`;
  }
  return "–";
}

function AuditOldNewCell({ row }: { row: EntriesAuditRow }) {
  const { t } = useTranslation();
  const oldR = row.oldRow;
  const newR = row.newRow;
  return (
    <div className="space-y-1 text-xs">
      {(row.source === "day_record" ||
        auditSnap(oldR, "total_hours") !== "–" ||
        auditSnap(newR, "total_hours") !== "–") && (
        <div>
          <span className="text-muted-foreground">{t("admin.auditHours")}</span>{" "}
          {auditSnap(oldR, "total_hours")} → {auditSnap(newR, "total_hours")}
          {(auditSnap(oldR, "absence_type") !== "–" || auditSnap(newR, "absence_type") !== "–") && (
            <>
              {" · "}
              <span className="text-muted-foreground">{t("admin.auditAbsence")}</span>{" "}
              {auditSnap(oldR, "absence_type")} → {auditSnap(newR, "absence_type")}
            </>
          )}
        </div>
      )}
      {(row.source === "time_entry" ||
        auditSnap(oldR, "rd_hours") !== "–" ||
        auditSnap(newR, "rd_hours") !== "–") && (
        <div>
          <span className="text-muted-foreground">{t("admin.auditRd")}</span>{" "}
          {auditSnap(oldR, "rd_hours")} → {auditSnap(newR, "rd_hours")}
        </div>
      )}
      {row.source === "week_note" && (newR?.note || oldR?.note) ? (
        <div className="whitespace-pre-wrap">
          <span className="text-muted-foreground">{t("admin.auditWeekDescription")}</span>{" "}
          {auditSnap(newR, "note") !== "–" ? auditSnap(newR, "note") : auditSnap(oldR, "note")}
        </div>
      ) : null}
      {row.source === "month_lock" && newR?.unlock_reason ? (
        <div>
          <span className="text-muted-foreground">{t("admin.auditReasonLabel")}</span>{" "}
          {auditSnap(newR, "unlock_reason")}
        </div>
      ) : null}
      {row.changeReason ? (
        <div>
          <span className="text-muted-foreground">{t("admin.auditReasonLabel")}</span>{" "}
          {row.changeReason}
        </div>
      ) : null}
    </div>
  );
}

function parseAdminLogPage(raw: string): number {
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
}

function useAdminLogPage(): [number, (next: number) => void] {
  const [pageRaw, setPageRaw] = useQueryParam("page", { fallback: "1" });
  const page = parseAdminLogPage(pageRaw);
  const setPage = (next: number) => setPageRaw(next <= 1 ? "1" : String(Math.max(1, next)));
  return [page, setPage];
}

/** URL `pageSize` coerced to the shared 20 / 50 / 100 options. */
function useAdminLogPageSize(): [PageSizeOption, (next: PageSizeOption) => void] {
  const [pageSizeRaw, setPageSizeRaw] = useQueryParam("pageSize", {
    fallback: String(DEFAULT_PAGE_SIZE),
  });
  const pageSize = coercePageSize(pageSizeRaw);
  const setPageSize = (next: PageSizeOption) => setPageSizeRaw(String(next));
  return [pageSize, setPageSize];
}

const SYSTEM_LOG_PARAM_FALLBACKS = {
  page: "1",
  pageSize: String(DEFAULT_PAGE_SIZE),
  q: "",
  eventType: "",
  from: "",
  to: "",
};

const SYSTEM_LOG_EVENT_TYPE_ALL = "all";
const SYSTEM_LOG_EVENT_TYPE_ALLOWED = ["", ...AUDIT_EVENT_TYPES] as const;

function AuditTab() {
  const { t } = useTranslation();
  const chain = useAdminAuditChain();
  const projects = useAdminProjects();
  const projectIds = (projects.data ?? []).map((row) => row.id);
  const [projectId] = useQueryParam("project", {
    fallback: "",
    allowed: projectIds.length > 0 ? projectIds : undefined,
  });
  const [page, setPage] = useAdminLogPage();
  const [pageSizeParam] = useAdminLogPageSize();
  const patchParams = usePatchQueryParams({
    project: "",
    page: "1",
    pageSize: String(DEFAULT_PAGE_SIZE),
  });
  const project = (projects.data ?? []).find((row) => row.id === projectId);
  const entriesAudit = useAdminEntriesAudit(projectId || undefined, {
    page,
    pageSize: pageSizeParam,
  });
  const download = useDownloadAuditXlsx();
  const rows = entriesAudit.data?.entries ?? [];
  const total = entriesAudit.data?.total ?? 0;
  const pageSize = coercePageSize(entriesAudit.data?.pageSize ?? pageSizeParam);

  useEffect(() => {
    if (!entriesAudit.data) return;
    const pages = pageCount(entriesAudit.data.total, entriesAudit.data.pageSize);
    if (page > pages) setPage(pages);
  }, [entriesAudit.data, page, setPage]);

  function setProjectId(next: string) {
    patchParams({ project: next || null, page: null });
  }

  /** One URL write — two useQueryParam setters race and drop pageSize (see usePatchQueryParams). */
  function onPageSizeChange(next: PageSizeOption) {
    patchParams({ pageSize: String(next), page: null });
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          {chain.isLoading && <Skeleton className="h-8 w-48" />}
          {chain.data && (
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant={chain.data.ok ? "default" : "destructive"}>
                {chain.data.ok ? t("admin.auditChainOk") : t("admin.auditChainFail")}
              </Badge>
              <span className="text-sm text-muted-foreground">
                {t("admin.auditChainRows", { count: chain.data.rowCount })}
              </span>
            </div>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!projectId || total === 0 || download.isPending}
          onClick={() =>
            download.mutate(projectId, {
              onSuccess: () => toast.success(t("admin.matrixDownloadDone")),
              onError: (error) => toast.error(describeError(error)),
            })
          }
        >
          <Download className="size-4" /> {t("admin.auditDownload")}
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">{t("admin.auditPickProject")}</Label>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="min-w-[260px]" aria-label={t("admin.auditPickProject")}>
              <SelectValue placeholder={t("admin.auditPickProject")} />
            </SelectTrigger>
            <SelectContent>
              <GroupedProjectSelectItems projects={projects.data ?? []} />
            </SelectContent>
          </Select>
        </div>
        {project && (
          <p className="pb-2 text-xs text-muted-foreground">
            {project.companyName} — {project.name}
          </p>
        )}
      </div>

      <section>
        <h2 className="text-lg">{t("admin.entriesAuditTitle")}</h2>
        {!projectId && (
          <p className="mt-3 text-sm text-muted-foreground">{t("admin.auditPickProjectHint")}</p>
        )}
        {projectId && entriesAudit.isLoading && (
          <Skeleton className="mt-4 h-40 w-full rounded-xl" />
        )}
        {projectId && !entriesAudit.isLoading && (
          <div className="mt-4 space-y-3">
            <PaginationBar
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={onPageSizeChange}
            />
            <div className={`max-h-[600px] overflow-auto ${adminTableClass()}`}>
              <Table data-testid="entries-audit-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("admin.auditWhen")}</TableHead>
                    <TableHead>{t("admin.auditAction")}</TableHead>
                    <TableHead>{t("admin.auditSource")}</TableHead>
                    <TableHead>{t("admin.auditDay")}</TableHead>
                    <TableHead>{t("admin.auditOldNew")}</TableHead>
                    <TableHead>{t("admin.auditEmployee")}</TableHead>
                    <TableHead>{t("admin.auditWho")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="entries-audit-tbody">
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-muted-foreground">
                        —
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((row) => (
                    <TableRow key={row.id} className="align-top" data-testid="entries-audit-row">
                      <TableCell className="whitespace-nowrap">
                        {formatTimestamp(row.changedAt)}
                      </TableCell>
                      <TableCell>{row.action}</TableCell>
                      <TableCell>{row.source}</TableCell>
                      <TableCell className="whitespace-nowrap">{auditDayCell(row)}</TableCell>
                      <TableCell>
                        <AuditOldNewCell row={row} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div>{row.employeeName ?? "–"}</div>
                        {row.employeeEmail ? (
                          <div className="text-[10px] text-muted-foreground">
                            {row.employeeEmail}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div>{row.changedByName ?? row.changedByRole ?? "–"}</div>
                        {row.changedByEmail ? (
                          <div className="text-[10px] text-muted-foreground">
                            {row.changedByEmail}
                          </div>
                        ) : null}
                        <div className="text-[10px] text-muted-foreground">
                          {[row.changedByRole, row.changedVia].filter(Boolean).join(" · ")}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function SystemLogsTab() {
  const { t } = useTranslation();
  const [page, setPage] = useAdminLogPage();
  const [pageSizeParam] = useAdminLogPageSize();
  const patchParams = usePatchQueryParams(SYSTEM_LOG_PARAM_FALLBACKS);
  const [q] = useQueryParam("q", { fallback: "" });
  const [eventType] = useQueryParam("eventType", {
    fallback: "",
    allowed: SYSTEM_LOG_EVENT_TYPE_ALLOWED,
  });
  const [fromRaw] = useQueryParam("from", { fallback: "" });
  const [toRaw] = useQueryParam("to", { fallback: "" });
  const from = isBusinessDate(fromRaw) ? fromRaw : "";
  const to = isBusinessDate(toRaw) ? toRaw : "";

  const auditEvents = useAdminAuditEvents({
    page,
    pageSize: pageSizeParam,
    q,
    eventType,
    from,
    to,
  });
  const downloadCsv = useDownloadAuditEventsCsv();
  const [detailRow, setDetailRow] = useState<AuditEventRow | null>(null);
  const rows = auditEvents.data?.events ?? [];
  const total = auditEvents.data?.total ?? 0;
  const pageSize = coercePageSize(auditEvents.data?.pageSize ?? pageSizeParam);

  useEffect(() => {
    if (!auditEvents.data) return;
    const pages = pageCount(auditEvents.data.total, auditEvents.data.pageSize);
    if (page > pages) setPage(pages);
  }, [auditEvents.data, page, setPage]);

  /** One URL write — two useQueryParam setters race and drop pageSize. */
  function onPageSizeChange(next: PageSizeOption) {
    patchParams({ pageSize: String(next), page: null });
  }

  function eventLabel(eventType: string, method: string | null): string {
    if (eventType === "login_succeeded" && method) {
      const methodLabel = t(`admin.auditLoginMethod.${method}`, { defaultValue: method });
      return t("admin.auditEvent.login_succeeded_with_method", { method: methodLabel });
    }
    return t(`admin.auditEvent.${eventType}`, { defaultValue: eventType });
  }
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg">{t("admin.auditEventsTitle")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("admin.auditEventsBody")}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          data-testid="audit-events-csv-export"
          disabled={total === 0 || downloadCsv.isPending}
          onClick={() =>
            downloadCsv.mutate(undefined, {
              onSuccess: () => toast.success(t("admin.matrixDownloadDone")),
              onError: (error) => toast.error(describeError(error)),
            })
          }
        >
          <Download className="size-4" /> {t("admin.auditEventsCsv")}
        </Button>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="system-logs-search">{t("admin.systemLogsSearch")}</Label>
          <Input
            id="system-logs-search"
            data-testid="system-logs-search"
            className="w-64"
            value={q}
            onChange={(event) => patchParams({ q: event.target.value || null, page: null })}
            placeholder={t("admin.systemLogsSearchPlaceholder")}
            aria-label={t("admin.systemLogsSearch")}
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t("admin.auditEventType")}</Label>
          <Select
            value={eventType || SYSTEM_LOG_EVENT_TYPE_ALL}
            onValueChange={(value) => {
              patchParams({
                eventType: value === SYSTEM_LOG_EVENT_TYPE_ALL ? null : value,
                page: null,
              });
            }}
          >
            <SelectTrigger
              className="min-w-[220px]"
              data-testid="system-logs-event-type"
              aria-label={t("admin.auditEventType")}
            >
              <SelectValue placeholder={t("admin.systemLogsEventTypeAll")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SYSTEM_LOG_EVENT_TYPE_ALL}>
                {t("admin.systemLogsEventTypeAll")}
              </SelectItem>
              {AUDIT_EVENT_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="system-logs-from">{t("admin.systemLogsFrom")}</Label>
          <Input
            id="system-logs-from"
            data-testid="system-logs-from"
            type="date"
            value={from}
            onChange={(event) => patchParams({ from: event.target.value || null, page: null })}
            aria-label={t("admin.systemLogsFrom")}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="system-logs-to">{t("admin.systemLogsTo")}</Label>
          <Input
            id="system-logs-to"
            data-testid="system-logs-to"
            type="date"
            value={to}
            onChange={(event) => patchParams({ to: event.target.value || null, page: null })}
            aria-label={t("admin.systemLogsTo")}
          />
        </div>
      </div>
      {auditEvents.isLoading && <Skeleton className="h-40 w-full rounded-xl" />}
      {!auditEvents.isLoading && (
        <div className="space-y-3">
          <PaginationBar
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={onPageSizeChange}
          />
          <div
            className={`max-h-[600px] overflow-auto ${adminTableClass()}`}
            data-testid="audit-events-scroller"
          >
            <Table data-testid="audit-events-table" containerClassName="overflow-visible">
              <TableHeader
                className="sticky top-0 z-10 bg-card [&_th]:bg-card"
                data-testid="audit-events-thead"
              >
                <TableRow>
                  <TableHead>{t("admin.auditWhen")}</TableHead>
                  <TableHead>{t("admin.auditEventType")}</TableHead>
                  <TableHead>{t("admin.auditBy")}</TableHead>
                  <TableHead>{t("admin.auditTarget")}</TableHead>
                  <TableHead>{t("admin.auditReason")}</TableHead>
                  <TableHead>{t("admin.auditFromTo")}</TableHead>
                  <TableHead>{t("admin.auditMethod")}</TableHead>
                  <TableHead>{t("admin.auditIp")}</TableHead>
                  <TableHead>{t("admin.auditMeta")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="audit-events-tbody">
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="text-muted-foreground"
                      data-testid="system-logs-empty"
                    >
                      {t("admin.systemLogsNoMatches")}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => {
                  const fromTo = row.from || row.to ? `${row.from ?? "—"} → ${row.to ?? "—"}` : "—";
                  return (
                    <TableRow key={row.id} data-testid="audit-events-row">
                      <TableCell className="whitespace-nowrap">
                        {formatTimestamp(row.createdAt)}
                      </TableCell>
                      <TableCell data-testid="audit-events-event">
                        <div>{eventLabel(row.eventType, row.method)}</div>
                        <div className="font-mono text-[10px] text-muted-foreground">
                          {row.eventType}
                        </div>
                      </TableCell>
                      <TableCell>
                        {row.actorEmail ?? row.actorName ?? row.actorUserId ?? "—"}
                      </TableCell>
                      <TableCell className="max-w-xs" data-testid="audit-events-target">
                        <div className="truncate">{row.targetResolved ?? "—"}</div>
                        {row.targetEmail && row.targetEmail !== row.targetResolved ? (
                          <div className="truncate text-[10px] text-muted-foreground">
                            {row.targetEmail}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell
                        className="max-w-[12rem] truncate"
                        data-testid="audit-events-reason"
                      >
                        {row.reason ?? "—"}
                      </TableCell>
                      <TableCell
                        className="max-w-[14rem] truncate"
                        data-testid="audit-events-from-to"
                      >
                        {fromTo}
                      </TableCell>
                      <TableCell data-testid="audit-events-method">
                        {row.method
                          ? t(`admin.auditLoginMethod.${row.method}`, { defaultValue: row.method })
                          : "—"}
                      </TableCell>
                      <TableCell data-testid="audit-events-ip">{row.ip ?? "—"}</TableCell>
                      <TableCell data-testid="audit-events-meta">
                        {auditMetaHasKeys(row.meta) ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            data-testid="audit-events-meta-detail"
                            onClick={() => setDetailRow(row)}
                          >
                            {t("admin.auditMetaDetails")}
                          </Button>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
      <Dialog open={detailRow !== null} onOpenChange={(open) => !open && setDetailRow(null)}>
        <DialogContent className="max-w-2xl" data-testid="audit-events-meta-dialog">
          {detailRow ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  {t("admin.auditMetaDialogTitle", {
                    event: eventLabel(detailRow.eventType, detailRow.method),
                    when: formatTimestamp(detailRow.createdAt),
                  })}
                </DialogTitle>
                <DialogDescription>{t("admin.auditMetaDialogBody")}</DialogDescription>
              </DialogHeader>
              <pre
                className="max-h-[50vh] overflow-auto whitespace-pre-wrap break-all rounded-md border border-border bg-muted/40 p-3 font-mono text-xs"
                data-testid="audit-events-meta-json"
              >
                {prettyAuditMeta(detailRow.meta)}
              </pre>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

type SmtpMailPublicValue = {
  user: string;
  from: string;
  replyTo: string | null;
  passwordConfigured: boolean;
};

type RateLimitValue = {
  attempts: number;
  window_seconds: number;
  lockout_seconds: number;
};

type TokenTtlValue = {
  login: number;
  invite: number;
  password_reset: number;
};

type EmailOutboxValue = {
  max_per_minute: number;
  daily_normal_cap: number;
  worker_interval_ms: number;
};

type RdShareLimitsValue = {
  min_percent: number;
  max_percent: number;
};

type WorkProfileValue = {
  max_weekly_hours: number;
  max_workdays_per_week: number;
  max_hours_per_workday: number;
};

type SettingRow = { key: string; value: unknown; updatedAt: string | null };

function parsePositiveInt(raw: string): number | null {
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1) return null;
  return n;
}

function parseNonNegInt(raw: string): number | null {
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 0) return null;
  return n;
}

function parsePositiveNumber(raw: string): number | null {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function settingValue<T>(rows: SettingRow[], key: string, fallback: T): T {
  const row = rows.find((item) => item.key === key);
  return (row?.value as T | undefined) ?? fallback;
}

function SettingNumberField(props: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  min?: number;
  max?: number;
  step?: number | string;
  testId?: string;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={props.id}>{props.label}</Label>
      <Input
        id={props.id}
        type="number"
        min={props.min}
        max={props.max}
        step={props.step ?? 1}
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        required
        data-testid={props.testId}
      />
      {props.hint ? <p className="text-xs text-muted-foreground">{props.hint}</p> : null}
    </div>
  );
}

function SettingsTab() {
  const { t } = useTranslation();
  const settings = useAdminSettings();
  const update = useAdminUpdateSetting();
  const [fieldDrafts, setFieldDrafts] = useState<Record<string, string>>({});
  const [smtpUserDraft, setSmtpUserDraft] = useState<string | null>(null);
  const [smtpPasswordDraft, setSmtpPasswordDraft] = useState("");

  function fieldValue(id: string, current: number): string {
    return fieldDrafts[id] ?? String(current);
  }

  function setField(id: string, value: string) {
    setFieldDrafts((prev) => ({ ...prev, [id]: value }));
  }

  function clearFields(...ids: string[]) {
    setFieldDrafts((prev) => {
      const next = { ...prev };
      for (const id of ids) delete next[id];
      return next;
    });
  }

  function saveSetting(key: string, value: unknown, clearIds: string[]) {
    update.mutate(
      { key, value },
      {
        onSuccess: () => {
          toast.success(t("admin.settingsSaved"));
          clearFields(...clearIds);
        },
        onError: (error) => toast.error(describeError(error)),
      },
    );
  }

  function invalid() {
    toast.error(t("admin.settingsInvalidValue"));
  }

  function saveWorkProfileLimits(current: WorkProfileValue) {
    const max_weekly_hours = parsePositiveNumber(fieldValue("wp.hours", current.max_weekly_hours));
    const max_workdays_per_week = parsePositiveInt(
      fieldValue("wp.days", current.max_workdays_per_week),
    );
    const max_hours_per_workday = parsePositiveNumber(
      fieldValue("wp.perDay", current.max_hours_per_workday),
    );
    if (
      max_weekly_hours == null ||
      max_weekly_hours > 80 ||
      max_workdays_per_week == null ||
      max_workdays_per_week > 7 ||
      max_hours_per_workday == null ||
      max_hours_per_workday > 24
    ) {
      invalid();
      return;
    }
    saveSetting(
      "work_profile_limits",
      { max_weekly_hours, max_workdays_per_week, max_hours_per_workday },
      ["wp.hours", "wp.days", "wp.perDay"],
    );
  }

  function saveSmtpMail(current: SmtpMailPublicValue) {
    const user = (smtpUserDraft ?? current.user).trim();
    if (!user) {
      invalid();
      return;
    }
    const password = smtpPasswordDraft;
    update.mutate(
      {
        key: "smtp_mail",
        value: {
          user,
          from: user,
          // Reply-To is env-only (SMTP_REPLY_TO); omit so UI never overwrites it.
          ...(password.length > 0 ? { password } : {}),
        },
      },
      {
        onSuccess: () => {
          toast.success(t("admin.settingsSaved"));
          setSmtpUserDraft(null);
          setSmtpPasswordDraft("");
        },
        onError: (error) => toast.error(describeError(error)),
      },
    );
  }

  function saveRateLimit(
    key: "login_rate_limit" | "magic_link_rate_limit",
    current: RateLimitValue,
  ) {
    const prefix = key === "login_rate_limit" ? "login" : "magic";
    const attempts = parsePositiveInt(fieldValue(`${prefix}.attempts`, current.attempts));
    const window_seconds = parsePositiveInt(fieldValue(`${prefix}.window`, current.window_seconds));
    const lockout_seconds = parseNonNegInt(
      fieldValue(`${prefix}.lockout`, current.lockout_seconds),
    );
    if (attempts == null || window_seconds == null || lockout_seconds == null) {
      invalid();
      return;
    }
    saveSetting(key, { attempts, window_seconds, lockout_seconds }, [
      `${prefix}.attempts`,
      `${prefix}.window`,
      `${prefix}.lockout`,
    ]);
  }

  function saveTokenTtl(current: TokenTtlValue) {
    const login = parsePositiveInt(fieldValue("ttl.login", current.login));
    const invite = parsePositiveInt(fieldValue("ttl.invite", current.invite));
    const password_reset = parsePositiveInt(fieldValue("ttl.reset", current.password_reset));
    if (login == null || invite == null || password_reset == null) {
      invalid();
      return;
    }
    saveSetting("auth_token_ttl_seconds", { login, invite, password_reset }, [
      "ttl.login",
      "ttl.invite",
      "ttl.reset",
    ]);
  }

  function saveEmailOutbox(current: EmailOutboxValue) {
    const max_per_minute = parsePositiveInt(fieldValue("outbox.max", current.max_per_minute));
    const daily_normal_cap = parsePositiveInt(fieldValue("outbox.daily", current.daily_normal_cap));
    const worker_interval_ms = parsePositiveInt(
      fieldValue("outbox.interval", current.worker_interval_ms),
    );
    if (max_per_minute == null || daily_normal_cap == null || worker_interval_ms == null) {
      invalid();
      return;
    }
    saveSetting("email_outbox", { max_per_minute, daily_normal_cap, worker_interval_ms }, [
      "outbox.max",
      "outbox.daily",
      "outbox.interval",
    ]);
  }

  function saveRdShareLimits(current: RdShareLimitsValue) {
    const min_percent = parsePositiveInt(fieldValue("rd.min", current.min_percent));
    const max_percent = parsePositiveInt(fieldValue("rd.max", current.max_percent));
    if (
      min_percent == null ||
      max_percent == null ||
      min_percent > 100 ||
      max_percent > 100 ||
      min_percent > max_percent
    ) {
      invalid();
      return;
    }
    saveSetting("rd_share_limits", { min_percent, max_percent }, ["rd.min", "rd.max"]);
  }

  if (settings.isLoading) return <Skeleton className="h-40 w-full rounded-xl" />;

  const rows = (settings.data ?? []) as SettingRow[];
  const workProfile = rows.find((row) => row.key === "work_profile_limits");
  const smtpMail = rows.find((row) => row.key === "smtp_mail");
  const loginRate = rows.find((row) => row.key === "login_rate_limit");
  const magicRate = rows.find((row) => row.key === "magic_link_rate_limit");
  const tokenTtl = rows.find((row) => row.key === "auth_token_ttl_seconds");
  const emailOutbox = rows.find((row) => row.key === "email_outbox");
  const rdShare = rows.find((row) => row.key === "rd_share_limits");

  const workProfileRaw = settingValue<WorkProfileValue>(rows, "work_profile_limits", {
    max_weekly_hours: 48,
    max_workdays_per_week: 5,
    max_hours_per_workday: 10,
  });
  const workProfileValue: WorkProfileValue = {
    ...workProfileRaw,
    max_hours_per_workday: workProfileRaw.max_hours_per_workday ?? 10,
  };
  const smtpValue = settingValue<SmtpMailPublicValue>(rows, "smtp_mail", {
    user: "",
    from: "",
    replyTo: null,
    passwordConfigured: false,
  });
  const loginRateValue = settingValue<RateLimitValue>(rows, "login_rate_limit", {
    attempts: 10,
    window_seconds: 900,
    lockout_seconds: 60,
  });
  const magicRateValue = settingValue<RateLimitValue>(rows, "magic_link_rate_limit", {
    attempts: 3,
    window_seconds: 900,
    lockout_seconds: 60,
  });
  const tokenTtlValue = settingValue<TokenTtlValue>(rows, "auth_token_ttl_seconds", {
    login: 259_200,
    invite: 604_800,
    password_reset: 1800,
  });
  const emailOutboxValue = settingValue<EmailOutboxValue>(rows, "email_outbox", {
    max_per_minute: 30,
    daily_normal_cap: 1800,
    worker_interval_ms: 2000,
  });
  const rdShareValue = settingValue<RdShareLimitsValue>(rows, "rd_share_limits", {
    min_percent: 1,
    max_percent: 95,
  });

  function rateLimitFields(prefix: "login" | "magic", current: RateLimitValue, testPrefix: string) {
    return (
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <SettingNumberField
          id={`${prefix}-attempts`}
          label={t("admin.rateLimitAttempts")}
          hint={t("admin.rateLimitAttemptsHint")}
          min={1}
          value={fieldValue(`${prefix}.attempts`, current.attempts)}
          onChange={(value) => setField(`${prefix}.attempts`, value)}
          testId={`${testPrefix}-attempts`}
        />
        <SettingNumberField
          id={`${prefix}-window`}
          label={t("admin.rateLimitWindow")}
          hint={t("admin.rateLimitWindowHint")}
          min={1}
          value={fieldValue(`${prefix}.window`, current.window_seconds)}
          onChange={(value) => setField(`${prefix}.window`, value)}
          testId={`${testPrefix}-window`}
        />
        <SettingNumberField
          id={`${prefix}-lockout`}
          label={t("admin.rateLimitLockout")}
          hint={t("admin.rateLimitLockoutHint")}
          min={0}
          value={fieldValue(`${prefix}.lockout`, current.lockout_seconds)}
          onChange={(value) => setField(`${prefix}.lockout`, value)}
          testId={`${testPrefix}-lockout`}
        />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <section className="space-y-4" data-testid="settings-email-section">
        <div>
          <h2 className="text-lg font-medium">{t("admin.settingsEmailSectionTitle")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("admin.settingsEmailSectionBody")}
          </p>
        </div>

        <form
          className="rounded-xl border border-border bg-card p-4"
          data-testid="smtp-settings-card"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            saveSmtpMail(smtpValue);
          }}
        >
          <h3 className="text-base font-medium">{t("admin.smtpMailTitle")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t("admin.smtpMailBody")}</p>
          {smtpMail?.updatedAt && (
            <p className="mt-1 text-xs text-muted-foreground">
              {t("admin.settingsUpdated")}: {formatTimestamp(smtpMail.updatedAt)}
            </p>
          )}
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="smtp-sender-email">{t("admin.smtpMailSender")}</Label>
              <Input
                id="smtp-sender-email"
                type="email"
                autoComplete="off"
                placeholder={t("admin.smtpMailSenderPlaceholder")}
                value={smtpUserDraft ?? smtpValue.user}
                onChange={(event) => setSmtpUserDraft(event.target.value)}
                required
                data-testid="smtp-settings-user"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="smtp-password">{t("admin.smtpMailPassword")}</Label>
              <Input
                id="smtp-password"
                type="password"
                autoComplete="new-password"
                placeholder={
                  smtpValue.passwordConfigured
                    ? t("admin.smtpMailPasswordKeep")
                    : t("admin.smtpMailPasswordPlaceholder")
                }
                value={smtpPasswordDraft}
                onChange={(event) => setSmtpPasswordDraft(event.target.value)}
                data-testid="smtp-settings-password"
              />
              <p className="text-xs text-muted-foreground">
                {smtpValue.passwordConfigured
                  ? t("admin.smtpMailPasswordConfigured")
                  : t("admin.smtpMailPasswordHint")}
              </p>
            </div>
          </div>
          <Button
            type="submit"
            className="mt-3"
            disabled={update.isPending}
            data-testid="smtp-settings-save"
          >
            {t("common.save")}
          </Button>
        </form>

        <form
          className="rounded-xl border border-border bg-card p-4"
          data-testid="email-outbox-settings-card"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            saveEmailOutbox(emailOutboxValue);
          }}
        >
          <h3 className="text-base font-medium">{t("admin.emailOutboxTitle")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t("admin.emailOutboxBody")}</p>
          {emailOutbox?.updatedAt && (
            <p className="mt-1 text-xs text-muted-foreground">
              {t("admin.settingsUpdated")}: {formatTimestamp(emailOutbox.updatedAt)}
            </p>
          )}
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <SettingNumberField
              id="outbox-max"
              label={t("admin.emailOutboxMaxPerMinute")}
              hint={t("admin.emailOutboxMaxPerMinuteHint")}
              min={1}
              value={fieldValue("outbox.max", emailOutboxValue.max_per_minute)}
              onChange={(value) => setField("outbox.max", value)}
              testId="email-outbox-max-per-minute"
            />
            <SettingNumberField
              id="outbox-daily"
              label={t("admin.emailOutboxDailyCap")}
              hint={t("admin.emailOutboxDailyCapHint")}
              min={1}
              value={fieldValue("outbox.daily", emailOutboxValue.daily_normal_cap)}
              onChange={(value) => setField("outbox.daily", value)}
              testId="email-outbox-daily-cap"
            />
            <SettingNumberField
              id="outbox-interval"
              label={t("admin.emailOutboxWorkerInterval")}
              hint={t("admin.emailOutboxWorkerIntervalHint")}
              min={1}
              value={fieldValue("outbox.interval", emailOutboxValue.worker_interval_ms)}
              onChange={(value) => setField("outbox.interval", value)}
              testId="email-outbox-worker-interval"
            />
          </div>
          <Button
            type="submit"
            className="mt-3"
            disabled={update.isPending}
            data-testid="email-outbox-settings-save"
          >
            {t("common.save")}
          </Button>
        </form>
      </section>

      <form
        className="rounded-xl border border-border bg-card p-4"
        data-testid="work-profile-settings-card"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          saveWorkProfileLimits(workProfileValue);
        }}
      >
        <h2 className="text-lg font-medium">{t("admin.workProfileLimitsTitle")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("admin.workProfileLimitsBody")}</p>
        {workProfile?.updatedAt && (
          <p className="mt-1 text-xs text-muted-foreground">
            {t("admin.settingsUpdated")}: {formatTimestamp(workProfile.updatedAt)}
          </p>
        )}
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <SettingNumberField
            id="setting-max-hours"
            label={t("admin.workProfileMaxHours")}
            min={1}
            max={80}
            step="0.5"
            value={fieldValue("wp.hours", workProfileValue.max_weekly_hours)}
            onChange={(value) => setField("wp.hours", value)}
            testId="work-profile-max-hours"
          />
          <SettingNumberField
            id="setting-max-days"
            label={t("admin.workProfileMaxDays")}
            min={1}
            max={7}
            value={fieldValue("wp.days", workProfileValue.max_workdays_per_week)}
            onChange={(value) => setField("wp.days", value)}
            testId="work-profile-max-days"
          />
          <SettingNumberField
            id="setting-max-per-day"
            label={t("admin.workProfileMaxHoursPerDay")}
            hint={t("admin.workProfileMaxHoursPerDayHint")}
            min={1}
            max={24}
            step="0.5"
            value={fieldValue("wp.perDay", workProfileValue.max_hours_per_workday)}
            onChange={(value) => setField("wp.perDay", value)}
            testId="work-profile-max-per-day"
          />
        </div>
        <Button type="submit" className="mt-3" disabled={update.isPending}>
          {t("common.save")}
        </Button>
      </form>

      <form
        className="rounded-xl border border-border bg-card p-4"
        data-testid="rd-share-settings-card"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          saveRdShareLimits(rdShareValue);
        }}
      >
        <h2 className="text-lg font-medium">{t("admin.rdShareLimitsTitle")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("admin.rdShareLimitsBody")}</p>
        {rdShare?.updatedAt && (
          <p className="mt-1 text-xs text-muted-foreground">
            {t("admin.settingsUpdated")}: {formatTimestamp(rdShare.updatedAt)}
          </p>
        )}
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <SettingNumberField
            id="rd-min"
            label={t("admin.rdShareMinPercent")}
            hint={t("admin.rdShareLimitsHint")}
            min={1}
            max={100}
            value={fieldValue("rd.min", rdShareValue.min_percent)}
            onChange={(value) => setField("rd.min", value)}
            testId="rd-share-min-percent"
          />
          <SettingNumberField
            id="rd-max"
            label={t("admin.rdShareMaxPercent")}
            min={1}
            max={100}
            value={fieldValue("rd.max", rdShareValue.max_percent)}
            onChange={(value) => setField("rd.max", value)}
            testId="rd-share-max-percent"
          />
        </div>
        <Button
          type="submit"
          className="mt-3"
          disabled={update.isPending}
          data-testid="rd-share-settings-save"
        >
          {t("common.save")}
        </Button>
      </form>

      <section className="space-y-4" data-testid="settings-rate-limits-section">
        <div>
          <h2 className="text-lg font-medium">{t("admin.rateLimitsSectionTitle")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("admin.rateLimitsSectionBody")}</p>
        </div>

        <form
          className="rounded-xl border border-border bg-card p-4"
          data-testid="login-rate-limit-card"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            saveRateLimit("login_rate_limit", loginRateValue);
          }}
        >
          <h3 className="text-base font-medium">{t("admin.loginRateLimitTitle")}</h3>
          {loginRate?.updatedAt && (
            <p className="mt-1 text-xs text-muted-foreground">
              {t("admin.settingsUpdated")}: {formatTimestamp(loginRate.updatedAt)}
            </p>
          )}
          {rateLimitFields("login", loginRateValue, "login-rate")}
          <Button
            type="submit"
            className="mt-3"
            disabled={update.isPending}
            data-testid="login-rate-limit-save"
          >
            {t("common.save")}
          </Button>
        </form>

        <form
          className="rounded-xl border border-border bg-card p-4"
          data-testid="magic-link-rate-limit-card"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            saveRateLimit("magic_link_rate_limit", magicRateValue);
          }}
        >
          <h3 className="text-base font-medium">{t("admin.magicLinkRateLimitTitle")}</h3>
          {magicRate?.updatedAt && (
            <p className="mt-1 text-xs text-muted-foreground">
              {t("admin.settingsUpdated")}: {formatTimestamp(magicRate.updatedAt)}
            </p>
          )}
          {rateLimitFields("magic", magicRateValue, "magic-rate")}
          <Button
            type="submit"
            className="mt-3"
            disabled={update.isPending}
            data-testid="magic-link-rate-limit-save"
          >
            {t("common.save")}
          </Button>
        </form>
      </section>

      <form
        className="rounded-xl border border-border bg-card p-4"
        data-testid="auth-token-ttl-card"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          saveTokenTtl(tokenTtlValue);
        }}
      >
        <h2 className="text-lg font-medium">{t("admin.authTokenTtlTitle")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("admin.authTokenTtlBody")}</p>
        {tokenTtl?.updatedAt && (
          <p className="mt-1 text-xs text-muted-foreground">
            {t("admin.settingsUpdated")}: {formatTimestamp(tokenTtl.updatedAt)}
          </p>
        )}
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <SettingNumberField
            id="ttl-login"
            label={t("admin.authTokenTtlLogin")}
            hint={t("admin.authTokenTtlLoginHint")}
            min={1}
            value={fieldValue("ttl.login", tokenTtlValue.login)}
            onChange={(value) => setField("ttl.login", value)}
            testId="auth-token-ttl-login"
          />
          <SettingNumberField
            id="ttl-invite"
            label={t("admin.authTokenTtlInvite")}
            hint={t("admin.authTokenTtlInviteHint")}
            min={1}
            value={fieldValue("ttl.invite", tokenTtlValue.invite)}
            onChange={(value) => setField("ttl.invite", value)}
            testId="auth-token-ttl-invite"
          />
          <SettingNumberField
            id="ttl-reset"
            label={t("admin.authTokenTtlPasswordReset")}
            hint={t("admin.authTokenTtlPasswordResetHint")}
            min={1}
            value={fieldValue("ttl.reset", tokenTtlValue.password_reset)}
            onChange={(value) => setField("ttl.reset", value)}
            testId="auth-token-ttl-password-reset"
          />
        </div>
        <Button
          type="submit"
          className="mt-3"
          disabled={update.isPending}
          data-testid="auth-token-ttl-save"
        >
          {t("common.save")}
        </Button>
      </form>

      <LegalSettingsSection />
    </div>
  );
}

function ImportsTab() {
  const { t } = useTranslation();
  const batches = useAdminImportBatches();
  const companies = useCompanies();
  const projects = useAdminProjects();
  const downloadTemplate = useDownloadHoursTemplate();
  const previewMut = useHoursImportPreview();
  const commitMut = useHoursImportCommit();

  const companyIds = (companies.data ?? []).map((row) => row.id);
  const [companyId] = useQueryParam("company", {
    fallback: "",
    allowed: companyIds.length > 0 ? companyIds : undefined,
  });
  const companyProjects = (projects.data ?? []).filter(
    (project) => project.companyId === companyId && !project.archived,
  );
  const importProjectIds = companyProjects.map((row) => row.id);
  const [projectId, setProjectId] = useQueryParam("project", {
    fallback: "",
    allowed: importProjectIds.length > 0 ? importProjectIds : undefined,
  });
  const patchQueryParams = usePatchQueryParams();
  const [preview, setPreview] = useState<HoursImportPreview | null>(null);

  if (batches.isLoading) return <Skeleton className="h-40 w-full rounded-xl" />;

  return (
    <div className="space-y-8">
      <section className={`${adminTableClass()} p-5`}>
        <h2 className="text-lg">{t("admin.hoursImportTitle")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("admin.hoursImportBody")}</p>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label>{t("admin.hoursImportCompany")}</Label>
            <Select
              value={companyId || undefined}
              onValueChange={(value) => {
                patchQueryParams({ company: value, project: "" });
                setPreview(null);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("admin.hoursImportPickCompany")} />
              </SelectTrigger>
              <SelectContent>
                {(companies.data ?? []).map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t("admin.hoursImportProject")}</Label>
            <Select
              value={projectId || "__none__"}
              onValueChange={(value) => setProjectId(value === "__none__" ? "" : value)}
              disabled={!companyId}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t("admin.hoursImportAllProjects")}</SelectItem>
                {companyProjects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <Button
              variant="outline"
              disabled={!companyId || downloadTemplate.isPending}
              onClick={() =>
                downloadTemplate.mutate(companyId, {
                  onError: (error) => toast.error(describeError(error)),
                })
              }
            >
              <Download className="size-4" /> {t("admin.hoursImportTemplate")}
            </Button>
            <Label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                disabled={!companyId || previewMut.isPending}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (!file || !companyId) return;
                  previewMut.mutate(
                    { companyId, projectId: projectId || null, file },
                    {
                      onSuccess: (data) => {
                        setPreview(data.preview);
                        toast.success(t("admin.hoursImportPreviewDone"));
                      },
                      onError: (error) => toast.error(describeError(error)),
                    },
                  );
                }}
              />
              {t("admin.hoursImportUpload")}
            </Label>
          </div>
        </div>

        {preview && (
          <div className="mt-6 space-y-4">
            <p className="text-sm">
              {t("admin.hoursImportTotals", {
                accepted: preview.totals.accepted,
                rejected: preview.totals.rejected,
                total: preview.totals.fileTotalHours,
                rd: preview.totals.fileRdHours,
              })}
            </p>
            {preview.rejected.length > 0 && (
              <div className="overflow-x-auto rounded-lg border border-border bg-card">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("admin.hoursImportRow")}</TableHead>
                      <TableHead>{t("admin.hoursImportEmail")}</TableHead>
                      <TableHead>{t("admin.hoursImportReason")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.rejected.slice(0, 50).map((row) => (
                      <TableRow key={`${row.rowNumber}-${row.reason}`}>
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell>{row.email || "—"}</TableCell>
                        <TableCell>{row.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <Button
              disabled={preview.rows.length === 0 || commitMut.isPending}
              onClick={() =>
                commitMut.mutate(
                  {
                    companyId: preview.companyId,
                    projectId: preview.projectId,
                    filename: preview.filename,
                    rows: preview.rows,
                  },
                  {
                    onSuccess: (result) => {
                      toast.success(
                        t("admin.hoursImportCommitted", {
                          inserted: result.stats.inserted,
                          updated: result.stats.updated,
                          unchanged: result.stats.unchanged,
                        }),
                      );
                      setPreview(null);
                    },
                    onError: (error) => toast.error(describeError(error)),
                  },
                )
              }
            >
              {t("admin.hoursImportCommit")}
            </Button>
          </div>
        )}
      </section>

      <div className={`overflow-x-auto ${adminTableClass()}`}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("admin.importsCreated")}</TableHead>
              <TableHead>{t("admin.importsKind")}</TableHead>
              <TableHead>{t("admin.importsCompany")}</TableHead>
              <TableHead>{t("admin.importsProject")}</TableHead>
              <TableHead>{t("admin.importsFilename")}</TableHead>
              <TableHead>{t("admin.importsDryRun")}</TableHead>
              <TableHead>{t("admin.importsStats")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(batches.data ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground">
                  {t("admin.importsEmpty")}
                </TableCell>
              </TableRow>
            )}
            {(batches.data ?? []).map((batch) => (
              <TableRow key={batch.id}>
                <TableCell>{formatTimestamp(batch.createdAt)}</TableCell>
                <TableCell>{batch.kind}</TableCell>
                <TableCell>{batch.companyName}</TableCell>
                <TableCell>{batch.projectName ?? "—"}</TableCell>
                <TableCell>{batch.filename}</TableCell>
                <TableCell>{batch.dryRun ? t("common.yes") : t("common.no")}</TableCell>
                <TableCell className="max-w-xs truncate font-mono text-xs">
                  {JSON.stringify(batch.stats)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return "—";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
